import { useEffect, useMemo, useRef, useState } from 'react';
import { useIcon } from '../lib/icons.js';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useServicesStore } from '../stores/servicesStore';
import { Star, MapPin, BadgeDollarSign, ShieldCheck, Clock, Tag, X, Search, Filter } from 'lucide-react';
import { slugify, initialsOf } from '../lib/slug';
import { getPublicDisplayName } from '../utils/displayName';
import { countryNameFromCode, COUNTRY_NAME_BY_CODE } from '../lib/countries';
import { apiGet, apiOrigin } from '../lib/api';
import { useCurrency } from '../context/CurrencyContext';
import countriesCitiesData from '../lib/countries-cities.json';
// AdvancedServiceFilters removed per request

// Normalize potentially relative image URLs coming from API
function normalizeImageUrl(src) {
  if (!src) return '';
  let s = String(src).trim();
  // strip wrapping quotes/backticks and spaces
  s = s.replace(/^[\s"'`]+|[\s"'`]+$/g, '');
  if (/^(data:|https?:)/i.test(s)) return s; // absolute or data url
  if (s.startsWith('//')) return `${window?.location?.protocol || 'https:'}${s}`;
  if (s.startsWith('/')) return `${apiOrigin}${s}`; // backend-served absolute path
  return `${apiOrigin}/${s}`; // likely backend relative path like uploads/foo.jpg
}
function normLower(s) {
  return String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

export default function AllServices() {
  const { t } = useTranslation();
  const location = useLocation();
  const { formatPrice, convert, currency: userCurrency } = useCurrency();
  const items = useServicesStore((s) => s.items);
  const loading = useServicesStore((s) => s.loading);
  const error = useServicesStore((s) => s.error);
  const search = useServicesStore((s) => s.search);
  const selectedCategory = useServicesStore((s) => s.selectedCategory);
  const selectedCategories = useServicesStore((s) => s.selectedCategories);
  const selectedLocation = useServicesStore((s) => s.selectedLocation);
  const selectedCountry = useServicesStore((s) => s.selectedCountry);
  const selectedCity = useServicesStore((s) => s.selectedCity);
  const minPrice = useServicesStore((s) => s.minPrice);
  const maxPrice = useServicesStore((s) => s.maxPrice);
  const minRating = useServicesStore((s) => s.minRating);
  const sortBy = useServicesStore((s) => s.sortBy);
  const categoriesList = useServicesStore((s) => s.categoriesList);
  const categoryMap = useServicesStore((s) => s.categoryMap);
  const loadingMore = useServicesStore((s) => s.loadingMore);
  const hasMore = useServicesStore((s) => s.hasMore);
  // const limit = useServicesStore((s) => s.limit);
  const initLoad = useServicesStore((s) => s.initLoad);
  const storeLoadMore = useServicesStore((s) => s.loadMore);
  const fetchCategories = useServicesStore((s) => s.fetchCategories);
  const setSearch = useServicesStore((s) => s.setSearch);
  const setSelectedCategory = useServicesStore((s) => s.setSelectedCategory);
  const setSelectedCategories = useServicesStore((s) => s.setSelectedCategories);
  const setSelectedLocation = useServicesStore((s) => s.setSelectedLocation);
  const setSelectedCountry = useServicesStore((s) => s.setSelectedCountry);
  const setSelectedCity = useServicesStore((s) => s.setSelectedCity);
  const setMinPrice = useServicesStore((s) => s.setMinPrice);
  const setMaxPrice = useServicesStore((s) => s.setMaxPrice);
  const setMinRating = useServicesStore((s) => s.setMinRating);
  const setSortBy = useServicesStore((s) => s.setSortBy);
  const resetFilters = useServicesStore((s) => s.resetFilters);
  const loaderRef = useRef(null);
  const [cityByIdMap, setCityByIdMap] = useState({});
  const [imgErrorById, setImgErrorById] = useState({});
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const FRONTEND_ORIGIN = typeof window !== 'undefined' && window.location ? window.location.origin : '';
  const hasParsedPathRef = useRef(false);
  const lastSyncedPathRef = useRef('');
  const [minPriceInput, setMinPriceInput] = useState(String(minPrice || ''));
  const [maxPriceInput, setMaxPriceInput] = useState(String(maxPrice || ''));
  useEffect(() => { setMinPriceInput(String(minPrice || '')); }, [minPrice]);
  useEffect(() => { setMaxPriceInput(String(maxPrice || '')); }, [maxPrice]);

  // Preserve focus and debounce SEO path updates for search
  const [searchDebounced, setSearchDebounced] = useState(search);
  const searchInputRef = useRef(null);
  const searchHadFocusRef = useRef(false);
  const searchSelectionRef = useRef({ start: null, end: null });
  useEffect(() => {
    const h = setTimeout(() => setSearchDebounced(search), 400);
    return () => clearTimeout(h);
  }, [search]);

  // Preserve focus and debounce SEO path updates for min/max price
  const [minPriceDebounced, setMinPriceDebounced] = useState(minPrice);
  const [maxPriceDebounced, setMaxPriceDebounced] = useState(maxPrice);
  const minInputRef = useRef(null);
  const maxInputRef = useRef(null);
  const minHadFocusRef = useRef(false);
  const maxHadFocusRef = useRef(false);
  const minSelectionRef = useRef({ start: null, end: null });
  const maxSelectionRef = useRef({ start: null, end: null });
  useEffect(() => {
    const h = setTimeout(() => setMinPriceDebounced(minPrice), 400);
    return () => clearTimeout(h);
  }, [minPrice]);
  useEffect(() => {
    const h = setTimeout(() => setMaxPriceDebounced(maxPrice), 400);
    return () => clearTimeout(h);
  }, [maxPrice]);

  const commitMinPrice = () => {
    const v = String(minPriceInput).trim();
    if (!v) { if (minPrice !== '') setMinPrice(''); return; }
    const n = Number(v);
    const next = Number.isFinite(n) ? n : '';
    if (minPrice !== next) setMinPrice(next);
  };
  const commitMaxPrice = () => {
    const v = String(maxPriceInput).trim();
    if (!v) { if (maxPrice !== '') setMaxPrice(''); return; }
    const n = Number(v);
    const next = Number.isFinite(n) ? n : '';
    if (maxPrice !== next) setMaxPrice(next);
  };

  // Load once on mount
  useEffect(() => {
    initLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch service categories for stable filter options and name mapping
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // NEW: Parse query params to pre-populate filters (deep links)
  useEffect(() => {
    // Only parse query params when present and not after our own path sync
    if (!location.search) return;
    if (hasParsedPathRef.current) return;
    const params = new URLSearchParams(location.search || '');
    const q = params.get('q');
    const cat = params.get('category');
    const cats = params.get('categories');
    const loc = params.get('location');
    const country = params.get('country');
    const city = params.get('city');
    const minP = params.get('minPrice');
    const maxP = params.get('maxPrice');
    const minR = params.get('minRating');

    if (q) setSearch(q);
    if (cat) {
      const nextCats = [cat];
      setSelectedCategories(nextCats);
      setSelectedCategory('');
    }
    if (cats) {
      const sep = cats.includes('~') ? '~' : ',';
      const nextCats = cats.split(sep).map((s) => s.trim()).filter(Boolean);
      setSelectedCategories(nextCats);
    }
    if (loc) setSelectedLocation(loc);
    if (country) setSelectedCountry(country);
    if (city) setSelectedCity(city);
    if (minP != null) setMinPrice(minP);
    if (maxP != null) setMaxPrice(maxP);
    if (minR != null) setMinRating(minR);
  }, [location.search, setSearch, setSelectedCategories, setSelectedCategory, setSelectedLocation, setSelectedCountry, setSelectedCity, setMinPrice, setMaxPrice, setMinRating]);

  // NEW: Parse SEO-friendly path segments (e.g., /category/design/country/sweden/city/stockholm/q/logo)
  useEffect(() => {
    const pathname = location.pathname || '';
    const marker = '/services/allservices';
    const idx = pathname.indexOf(marker);
    const tail = idx >= 0 ? pathname.slice(idx + marker.length) : '';
    const segs = tail.split('/').filter(Boolean);

    // If this pathname was just set by our sync effect, skip re-parsing to avoid loops
    if (lastSyncedPathRef.current && pathname === lastSyncedPathRef.current) {
      return;
    }

    if (segs.length === 0) { hasParsedPathRef.current = true; return; }

    // Clear filters only when actual segments are present
    setSearch('');
    setSelectedCategory('');
    setSelectedCategories([]);
    setSelectedCountry('');
    setSelectedCity('');
    setMinPrice('');
    setMaxPrice('');
    setMinRating('');
    setSortBy('recommended');

    const catsFromPath = [];
    let countryFromPath = '';
    let cityFromPath = '';
    let qFromPath = '';
    let minPriceFromPath = null;
    let maxPriceFromPath = null;
    let minRatingFromPath = null;
    let sortFromPath = '';
    const sortMapFromSlug = {
      'price-low-to-high': 'priceLowToHigh',
      'price-high-to-low': 'priceHighToLow',
      'highest-rated': 'highestRated',
      'recommended': 'recommended',
    };
    
    // Build a slug -> name map for categories and subcategories
    const allCats = Array.isArray(categoriesList) ? categoriesList : [];
    const nameBySlug = new Map();
    allCats.forEach((c) => {
      const nm = (c?.name || c?.title || '').trim();
      if (nm) nameBySlug.set(slugify(nm), nm);
      const kids = Array.isArray(c?.subcategories) ? c.subcategories : [];
      kids.forEach((sc) => {
        const sn = (sc?.name || sc?.title || '').trim();
        if (sn) nameBySlug.set(slugify(sn), sn);
      });
    });

    for (let i = 0; i < segs.length; i += 2) {
      const key = segs[i];
      const val = decodeURIComponent(segs[i + 1] || '');
      if (!key) break;
      if (key === 'category') {
        // Single category per segment; push to array
        const nm = nameBySlug.get(val) || val.replace(/-/g, ' ');
        if (nm) catsFromPath.push(nm);
      } else if (key === 'categories') {
        // Tilde-separated list (also support comma for back-compat)
        const sep = val.includes('~') ? '~' : ',';
        const parts = val.split(sep).filter(Boolean);
        parts.forEach((p) => {
          const nm = nameBySlug.get(p) || p.replace(/-/g, ' ');
          if (nm) catsFromPath.push(nm);
        });
      } else if (key === 'country') {
        countryFromPath = val.replace(/-/g, ' ');
      } else if (key === 'city') {
        cityFromPath = val.replace(/-/g, ' ');
      } else if (key === 'q') {
        qFromPath = val.replace(/-/g, ' ');
      } else if (key === 'min-price') {
        const num = Number(val);
        if (Number.isFinite(num)) minPriceFromPath = num;
      } else if (key === 'max-price') {
        const num = Number(val);
        if (Number.isFinite(num)) maxPriceFromPath = num;
      } else if (key === 'rating') {
        const num = Number(val);
        if (Number.isFinite(num) && num > 0) minRatingFromPath = num;
      } else if (key === 'sort') {
        const lower = String(val || '').toLowerCase();
        sortFromPath = sortMapFromSlug[lower] || '';
      } else {
        // unknown key; stop parsing to avoid misalignment
        break;
      }
    }

    if (qFromPath) setSearch(qFromPath);
    // Always use multi-select for categories from path
    if (catsFromPath.length > 0) setSelectedCategories(catsFromPath);
    setSelectedCategory('');
    if (countryFromPath) setSelectedCountry(countryFromPath);
    if (cityFromPath) setSelectedCity(cityFromPath);
    if (minPriceFromPath != null) setMinPrice(minPriceFromPath);
    if (maxPriceFromPath != null) setMaxPrice(maxPriceFromPath);
    if (minRatingFromPath != null) setMinRating(minRatingFromPath);
    if (sortFromPath) setSortBy(sortFromPath);
    hasParsedPathRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, categoriesList]);
  
  // NEW: Keep URL in sync with current filters using SEO-friendly path
  useEffect(() => {
    if (!hasParsedPathRef.current) return;
    // const base = '/services/allservices';
    const segs = [];

    // Build SEO-friendly path segments
    const toSlug = (s) => slugify(String(s || '').trim());
    const cats = Array.isArray(selectedCategories) ? selectedCategories.filter(Boolean) : [];
    if (cats.length > 0) {
      // Canonical representation for categories: tilde-separated list (cleaner URLs)
      segs.push('categories', cats.map(toSlug).join('~'));
    }
    if (selectedCountry) segs.push('country', String(selectedCountry).toLowerCase().replace(/\s+/g, '-'));
    if (selectedCity) segs.push('city', String(selectedCity).toLowerCase().replace(/\s+/g, '-'));
    if (searchDebounced) segs.push('q', String(searchDebounced).toLowerCase().replace(/\s+/g, '-'));

    // Only include numeric filters when valid numbers are set
    const toNum = (v) => { if (v === '' || v == null) return undefined; const n = Number(v); return Number.isFinite(n) ? n : undefined; };
    const minPNum = toNum(minPriceDebounced);
    const maxPNum = toNum(maxPriceDebounced);
    const minRNum = toNum(minRating);
    if (minPNum !== undefined) segs.push('min-price', String(minPNum));
    if (maxPNum !== undefined) segs.push('max-price', String(maxPNum));
    if (minRNum !== undefined && minRNum > 0) segs.push('rating', String(minRNum));

    const sortSlug = (v) => {
      if (!v || v === 'recommended') return null;
      if (v === 'priceLowToHigh') return 'price-low-to-high';
      if (v === 'priceHighToLow') return 'price-high-to-low';
      if (v === 'highestRated') return 'highest-rated';
      return String(v).toLowerCase().replace(/\s+/g, '-');
    };
    const sortSeg = sortSlug(sortBy);
    if (sortSeg) segs.push('sort', sortSeg);

    const path = '/services/allservices' + (segs.length ? '/' + segs.join('/') : '');

    // Build final path including debounced search for SEO
    const nextUrl = path;

    const currentUrl = location.pathname + (location.search || '');
    if (currentUrl !== nextUrl) {
      lastSyncedPathRef.current = nextUrl;
      if (typeof window !== 'undefined' && window.history) {
        try { window.history.replaceState(null, '', nextUrl); } catch (e) { void e; }
      }
      if (searchHadFocusRef.current && searchInputRef.current) {
        requestAnimationFrame(() => {
          const { start, end } = searchSelectionRef.current || {};
          searchInputRef.current.focus({ preventScroll: true });
          if (typeof start === 'number' && typeof end === 'number') {
            try { searchInputRef.current.setSelectionRange(start, end); } catch (e) { void e; }
          }
        });
      }
      if (minHadFocusRef.current && minInputRef.current) {
        requestAnimationFrame(() => {
          const { start, end } = minSelectionRef.current || {};
          minInputRef.current.focus({ preventScroll: true });
          if (typeof start === 'number' && typeof end === 'number') {
            try { minInputRef.current.setSelectionRange(start, end); } catch (e) { void e; }
          }
        });
      }
      if (maxHadFocusRef.current && maxInputRef.current) {
        requestAnimationFrame(() => {
          const { start, end } = maxSelectionRef.current || {};
          maxInputRef.current.focus({ preventScroll: true });
          if (typeof start === 'number' && typeof end === 'number') {
            try { maxInputRef.current.setSelectionRange(start, end); } catch (e) { void e; }
          }
        });
      }
    }
 }, [searchDebounced, selectedCategory, selectedCategories, selectedCountry, selectedCity, minPriceDebounced, maxPriceDebounced, minRating, sortBy, location.pathname, location.search]);

  // Infinite scroll using IntersectionObserver
  useEffect(() => {
    const el = loaderRef.current;
    if (!el) return;
    const observer = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (entry.isIntersecting) {
        storeLoadMore();
      }
    }, { root: null, rootMargin: '200px', threshold: 0 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [loaderRef, hasMore, loadingMore, storeLoadMore]);

  // Resolve city names via city_id for services in view (cached)
  useEffect(() => {
    let active = true;
    const ids = new Set();
    (Array.isArray(items) ? items : []).forEach((s) => {
      const c = s?.creative || {};
      const u = c?.user || s?.user || {};
      const cid = u?.city_id || s?.city_id || c?.city_id;
      if (cid) ids.add(String(cid));
    });
    const missing = Array.from(ids).filter((id) => !cityByIdMap[id]);
    if (missing.length === 0) return;
    (async () => {
      try {
        const results = await Promise.all(
          missing.map(async (id) => {
            try {
              const payload = await apiGet(`/cities/${id}`);
              const cityRec = payload?.data || payload?.city || payload;
              const name = cityRec?.name || cityRec?.data?.name || '';
              return [id, name];
            } catch (e) {
              void e;
              return [id, ''];
            }
          })
        );
        if (!active) return;
        setCityByIdMap((prev) => {
          const next = { ...prev };
          results.forEach(([id, name]) => { if (id && name) next[id] = name; });
          return next;
        });
      } catch (e) {
        void e;
        // noop; best-effort cache fill
      }
    })();
    return () => { active = false; };
  }, [items, cityByIdMap]);

  // Derived options
  const categoryOptions = useMemo(() => {
    const names = (Array.isArray(categoriesList) ? categoriesList : []).map((c) => c.name || c.title).filter(Boolean);
    return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b));
  }, [categoriesList]);
  // Normalize selectedCategory to canonical option (case/slug-insensitive)
  useEffect(() => {
    if (!selectedCategory) return;
    if (!Array.isArray(categoryOptions) || categoryOptions.length === 0) return;
    const lower = normLower(selectedCategory);
    const byLower = categoryOptions.find((opt) => normLower(opt) === lower);
    if (byLower && byLower !== selectedCategory) {
      setSelectedCategory(byLower);
      return;
    }
    const bySlug = categoryOptions.find((opt) => slugify(opt) === slugify(selectedCategory));
    if (bySlug && bySlug !== selectedCategory) {
      setSelectedCategory(bySlug);
    }
  }, [selectedCategory, categoryOptions, setSelectedCategory]);
  // Normalize selectedCategories entries to canonical options
  useEffect(() => {
    if (!Array.isArray(selectedCategories) || selectedCategories.length === 0) return;
    if (!Array.isArray(categoryOptions) || categoryOptions.length === 0) return;
    const canonical = selectedCategories.map((cat) => {
      const lower = normLower(cat);
      const byLower = categoryOptions.find((opt) => normLower(opt) === lower);
      if (byLower) return byLower;
      const bySlug = categoryOptions.find((opt) => slugify(opt) === slugify(cat));
      if (bySlug) return bySlug;
      return cat;
    });
    // Update only if changed
    const changed = canonical.some((c, i) => c !== (selectedCategories || [])[i]);
    if (changed) setSelectedCategories(canonical);
  }, [selectedCategories, categoryOptions, setSelectedCategories]);
  // Reset category filter if selection is not present in available options (only when options exist)
  useEffect(() => {
    if (!selectedCategory) return;
    if (!Array.isArray(categoryOptions) || categoryOptions.length === 0) return;
    const isValid = categoryOptions.some((opt) => normLower(opt) === normLower(selectedCategory) || slugify(opt) === slugify(selectedCategory));
    if (!isValid) {
      setSelectedCategory('');
    }
  }, [categoryOptions, selectedCategory, setSelectedCategory]);

  

  const countryOptions = useMemo(() => {
    const NORDIC_CODES = ['SE', 'NO', 'DK', 'FI', 'IS'];
    return NORDIC_CODES
      .map((code) => COUNTRY_NAME_BY_CODE[code])
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
  }, []);

  const citiesByCountry = useMemo(() => {
    const map = new Map();
    (Array.isArray(items) ? items : []).forEach((s) => {
      const tUser = s?.creative?.user || s?.user || {};
      const rawCity = s.city_name || tUser?.city_name || tUser?.city || s.city || s.cityName || '';
      const countryCode = tUser?.country_code || s?.creative?.country_code || '';
      const countryFull = countryNameFromCode(countryCode);
      const key = String(countryFull || '').toLowerCase();
      if (!key) return;
      if (!map.has(key)) map.set(key, new Set());
      if (rawCity) map.get(key).add(String(rawCity));
    });
    return map;
  }, [items]);

  const staticCitiesByCountry = useMemo(() => {
    const m = new Map();
    const arr = Array.isArray(countriesCitiesData?.countries) ? countriesCitiesData.countries : [];
    arr.forEach((c) => {
      const key = String(c?.name || '').trim().toLowerCase();
      if (!key) return;
      const names = new Set((Array.isArray(c?.cities) ? c.cities : []).map((ct) => String(ct?.name || '').trim()).filter(Boolean));
      m.set(key, names);
    });
    return m;
  }, []);

  const cityOptions = useMemo(() => {
    if (!selectedCountry) return [];
    const key = String(selectedCountry).toLowerCase();
    const dyn = citiesByCountry.get(key) || new Set();
    const stat = staticCitiesByCountry.get(key) || new Set();
    const merged = new Set([...Array.from(dyn), ...Array.from(stat)]);
    return Array.from(merged).sort((a, b) => a.localeCompare(b));
  }, [selectedCountry, citiesByCountry, staticCitiesByCountry]);

  // Normalize selected country to canonical casing from options
  useEffect(() => {
    if (!selectedCountry) return;
    const lower = normLower(selectedCountry);
    const match = countryOptions.find((c) => normLower(c) === lower);
    if (match && match !== selectedCountry) setSelectedCountry(match);
  }, [selectedCountry, countryOptions, setSelectedCountry]);

  // Normalize selected city to canonical casing from options
  useEffect(() => {
    if (!selectedCity) return;
    const lower = normLower(selectedCity);
    const match = cityOptions.find((c) => normLower(c) === lower);
    if (match && match !== selectedCity) setSelectedCity(match);
  }, [selectedCity, cityOptions, setSelectedCity]);

  // Adjusted: infer country from selected city; only clear if invalid for selected country
  useEffect(() => {
    if (!selectedCity) return;
    const selCityNorm = normLower(selectedCity);

    // If no country selected, best-effort infer country from static/dynamic maps
    if (!selectedCountry) {
      let inferred = '';
      // Search static list first
      staticCitiesByCountry.forEach((cities, country) => {
        if (inferred) return;
        const match = Array.from(cities).some((nm) => normLower(nm) === selCityNorm);
        if (match) inferred = country;
      });
      // Fallback to dynamic map from items
      if (!inferred) {
        citiesByCountry.forEach((cities, country) => {
          if (inferred) return;
          const match = Array.from(cities).some((nm) => normLower(nm) === selCityNorm);
          if (match) inferred = country;
        });
      }
      if (inferred) {
        setSelectedCountry(inferred);
        return; // keep city; URL sync will handle updates
      }
      // If we cannot infer, do not clear the city; wait for user to choose a country
      return;
    }

    // With a selected country, clear city only if it is not valid for that country (case-insensitive)
    if (Array.isArray(cityOptions) && cityOptions.length > 0) {
      const validLower = new Set(cityOptions.map((c) => normLower(c)));
      if (!validLower.has(normLower(selectedCity))) {
        setSelectedCity('');
      }
    }
  }, [selectedCountry, selectedCity, cityOptions, staticCitiesByCountry, citiesByCountry, setSelectedCountry, setSelectedCity]);

  

  const catTree = useMemo(() => {
    const list = Array.isArray(categoriesList) ? categoriesList : [];
    const parents = [];
    const byParent = new Map();
    list.forEach((c) => {
      const pid = c.parent_id || c.parentId || null;
      if (pid) {
        if (!byParent.has(pid)) byParent.set(pid, []);
        byParent.get(pid).push(c);
      } else {
        parents.push(c);
      }
    });
    return parents.map((p) => {
      const pName = (p.name || p.title || '').trim();
      const kidsRaw = (Array.isArray(p.subcategories) ? p.subcategories : (byParent.get(p.id) || []));
      const kids = (kidsRaw || []).filter((sc) => {
        const scName = (sc.name || sc.title || '').trim();
        return scName && scName !== pName; // hide duplicate child with same name as parent
      });
      return { parent: p, children: kids };
    });
  }, [categoriesList]);
  const categoryCounts = useMemo(() => {
    const counts = {};
    (Array.isArray(items) ? items : []).forEach((s) => {
      const name = (s?.category_id && categoryMap?.[s.category_id]) || s?.category || s?.subcategory || s?.mainCategory || '';
      if (!name) return;
      const k = String(name);
      counts[k] = (counts[k] || 0) + 1;
    });
    return counts;
  }, [items, categoryMap]);

  const childrenByParentLower = useMemo(() => {
    const m = new Map();
    (Array.isArray(catTree) ? catTree : []).forEach(({ parent, children }) => {
      const pName = String((parent?.name || parent?.title || '')).trim().toLowerCase();
      if (!pName) return;
      const set = new Set((children || []).map((sc) => String((sc?.name || sc?.title || '')).trim().toLowerCase()).filter(Boolean));
      m.set(pName, set);
    });
    return m;
  }, [catTree]);

  const categoryCountsAgg = useMemo(() => {
    const agg = {};
    (Array.isArray(catTree) ? catTree : []).forEach(({ parent, children }) => {
      const pName = (parent?.name || parent?.title || '').trim();
      if (!pName) return;
      let total = Number(categoryCounts[pName] || 0);
      (children || []).forEach((sc) => {
        const scName = (sc?.name || sc?.title || '').trim();
        if (scName) total += Number(categoryCounts[scName] || 0);
      });
      agg[pName] = total;
    });
    return agg;
  }, [catTree, categoryCounts]);

  // NEW: Parse free-form search phrase to auto-apply filters (categories, country/city, price, rating, sort)
  useEffect(() => {
    const phrase = String(searchDebounced || '').trim();
    if (!phrase) return;

    // Helper normalizers
    const norm = (s) => String(s || '').trim().toLowerCase();
    const toWords = (s) => s
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-ZÅÄÖåäö0-9\s-]/g, ' ')
      .split(/\s+/)
      .filter(Boolean);

    // Build category lookup (canonical names)
    const catNames = Array.isArray(categoryOptions) ? categoryOptions : [];
    const catByNorm = new Map();
    catNames.forEach((nm) => { const n = norm(nm); if (n) catByNorm.set(n, nm); });
    // Include children from tree
    (Array.isArray(catTree) ? catTree : []).forEach(({ parent, children }) => {
      const pName = (parent?.name || parent?.title || '').trim();
      const pNorm = norm(pName);
      if (pNorm && !catByNorm.has(pNorm)) catByNorm.set(pNorm, pName);
      (children || []).forEach((sc) => {
        const scName = (sc?.name || sc?.title || '').trim();
        const scNorm = norm(scName);
        if (scNorm && !catByNorm.has(scNorm)) catByNorm.set(scNorm, scName);
      });
    });

    // Synonyms for common categories
    const synonymsRaw = {
      writer: ['writer','copywriter','copywriting','content writer','journalist'],
      editor: ['editor','editing','video editor'],
      photographer: ['photographer','photography','photo'],
      videographer: ['videographer','videography','filmmaker','video'],
      designer: ['designer','design','ui','ux','ui ux','graphic','logo designer','logo','product designer','web design','web designer'],
      seo: ['seo','search engine optimization','sem'],
      marketer: ['marketing','marketer','digital marketer','social media','smm'],
      branding: ['branding','brand','brand strategist','art director','creative director'],
      developer: ['developer','frontend','backend','full stack','web developer','app developer'],
    };

    const words = toWords(phrase);
    const text = norm(phrase);

    // Detect price range
    let minPriceFromPhrase = null;
    let maxPriceFromPhrase = null;
    // Examples: "under 500", "below 1000", "max 300", "over 200", "above 200", "min 100" "between 200 and 400"
    const priceMatches = Array.from(text.matchAll(/(under|below|max)\s+(\d{2,6})/gi));
    const minMatches = Array.from(text.matchAll(/(over|above|min)\s+(\d{2,6})/gi));
    const betweenMatch = text.match(/between\s+(\d{2,6})\s+(and|to|-)\s+(\d{2,6})/i);
    if (betweenMatch) {
      const a = Number(betweenMatch[1]);
      const b = Number(betweenMatch[3]);
      if (Number.isFinite(a) && Number.isFinite(b)) {
        minPriceFromPhrase = Math.min(a, b);
        maxPriceFromPhrase = Math.max(a, b);
      }
    }
    if (priceMatches.length > 0) {
      const n = Number(priceMatches[priceMatches.length - 1][2]);
      if (Number.isFinite(n)) maxPriceFromPhrase = n;
    }
    if (minMatches.length > 0) {
      const n = Number(minMatches[minMatches.length - 1][2]);
      if (Number.isFinite(n)) minPriceFromPhrase = n;
    }

    // Detect rating e.g., "4 star", "rating 4+", "at least 3"
    let minRatingFromPhrase = null;
    const ratingMatch = text.match(/(\brating\b|\bstar\b|\bstars\b|\bat\s+least\b)\s*(\d(?:\.\d)?)\+?/i);
    if (ratingMatch) {
      const r = Number(ratingMatch[2]);
      if (Number.isFinite(r) && r > 0) minRatingFromPhrase = Math.min(5, Math.max(0, r));
    }

    // Detect sort keywords
    let sortFromPhrase = '';
    if (/cheapest|lowest\s+price|low\s+to\s+high/i.test(text)) sortFromPhrase = 'priceLowToHigh';
    else if (/expensive|highest\s+price|high\s+to\s+low/i.test(text)) sortFromPhrase = 'priceHighToLow';
    else if (/highest\s+rated|top\s+rated|best\s+rating/i.test(text)) sortFromPhrase = 'highestRated';

    // Detect country and city (static list + dynamic map)
    let countryFromPhrase = '';
    let cityFromPhrase = '';
    // Countries (Nordic set already available)
    const countriesLower = (Array.isArray(countryOptions) ? countryOptions : []).map(norm);
    countriesLower.forEach((c) => { if (!countryFromPhrase && text.includes(c)) countryFromPhrase = c; });
    // Cities
    const staticCities = new Map(staticCitiesByCountry);
    staticCities.forEach((cities, country) => {
      const cnorm = norm(country);
      cities.forEach((city) => {
        const ctn = norm(city);
        if (!cityFromPhrase && (text.includes(ctn) || words.includes(ctn))) {
          cityFromPhrase = city; // keep original-cased
          if (!countryFromPhrase) countryFromPhrase = cnorm;
        }
      });
    });

    // Detect categories via direct match or synonyms
    const foundCategories = new Set();
    // direct matches
    catByNorm.forEach((orig, key) => { if (text.includes(key)) foundCategories.add(orig); });
    // synonyms
    Object.entries(synonymsRaw).forEach(([canonical, arr]) => {
      const cNorm = norm(canonical);
      const normArr = Array.from(new Set(arr.map(norm)));
      const match = normArr.some((syn) => text.includes(syn));
      if (!match) return;
      // Prefer direct canonical category match
      const exact = catByNorm.get(cNorm);
      if (exact) {
        foundCategories.add(exact);
        return;
      }
      // Otherwise, map any synonym directly to an existing category name
      normArr.forEach((syn) => {
        const cat = catByNorm.get(syn);
        if (cat) foundCategories.add(cat);
      });
      // Fallback fuzzy: if no direct synonym keys exist, try substring/root overlap
      if (foundCategories.size === 0) {
        const stems = normArr.map((s) => s.replace(/ers$/,'er').replace(/ists$/,'ist').replace(/s$/,'').replace(/ing$/,'').replace(/er$/,''));
        catByNorm.forEach((orig, key) => {
          const kStem = key.replace(/ers$/,'er').replace(/ists$/,'ist').replace(/s$/,'').replace(/ing$/,'').replace(/er$/,'');
          if (stems.some((st) => kStem.includes(st) || st.includes(kStem))) {
            foundCategories.add(orig);
          }
        });
      }
    });

    // Fuzzy token-overlap: infer best matching categories from phrase tokens
    // Build normalized phrase tokens with simple stemming
    const normalizeWord = (w) => {
      let x = String(w || '').toLowerCase();
      x = x.replace(/&/g, ' ');
      x = x.replace(/\//g, ' ');
      x = x.replace(/ers$/,'er');
      x = x.replace(/ists$/,'ist');
      x = x.replace(/s$/,'');
      x = x.replace(/ing$/,'');
      x = x.replace(/er$/,'');
      return x.trim();
    };
    const phraseTokens = text
      .replace(/[^a-zA-ZÅÄÖåäö-\s]/g, ' ')
      .split(/\s+/)
      .filter(Boolean)
      .map(normalizeWord);
    const phraseSet = new Set(phraseTokens);
    // Expand phrase tokens with synonyms
    const synMap = Object.fromEntries(Object.entries(synonymsRaw).map(([k, arr]) => [normalizeWord(k), Array.from(new Set(arr.map(normalizeWord)))]));
    phraseTokens.forEach((qt) => {
      Object.entries(synMap).forEach(([key, arr]) => {
        if (qt === key || arr.includes(qt)) arr.forEach((syn) => phraseSet.add(syn));
      });
    });

    // Precompute category token sets from slugs (parent + children)
    const categoryTokenSets = [];
    catByNorm.forEach((origName) => {
      const slug = slugify(origName);
      const toks = slug.split('-').filter(Boolean).map(normalizeWord);
      categoryTokenSets.push([origName, new Set(toks)]);
    });
    // Score categories by overlap with phrase tokens
    let bestScore = 0;
    const matchedByFuzzy = new Set();
    categoryTokenSets.forEach(([orig, catSet]) => {
      let present = 0;
      catSet.forEach((t) => { if (phraseSet.has(t)) present++; });
      const score = present / Math.max(1, catSet.size);
      if (present >= 2 || score >= 0.66) {
        matchedByFuzzy.add(orig);
        if (score > bestScore) bestScore = score;
      }
    });
    matchedByFuzzy.forEach((nm) => foundCategories.add(nm));

    // Apply filters: only set ones that are not already chosen by user
    if (foundCategories.size > 0) {
      const existing = new Set(Array.isArray(selectedCategories) ? selectedCategories : []);
      const merged = new Set([...existing, ...Array.from(foundCategories)]);
      if (merged.size !== existing.size) {
        setSelectedCategories(Array.from(merged));
        setSelectedCategory('');
      }
    }
    if (countryFromPhrase && !selectedCountry) {
      // Map normalized back to canonical casing
      const match = (Array.isArray(countryOptions) ? countryOptions : []).find((c) => norm(c) === countryFromPhrase);
      if (match) setSelectedCountry(match);
    }
    if (cityFromPhrase && !selectedCity) {
      setSelectedCity(cityFromPhrase);
    }
    if (minPriceFromPhrase != null && (minPrice === '' || minPrice == null)) setMinPrice(minPriceFromPhrase);
    if (maxPriceFromPhrase != null && (maxPrice === '' || maxPrice == null)) setMaxPrice(maxPriceFromPhrase);
    if (minRatingFromPhrase != null && (minRating === '' || minRating == null)) setMinRating(minRatingFromPhrase);
    if (sortFromPhrase && !sortBy) setSortBy(sortFromPhrase);
  }, [searchDebounced, categoryOptions, catTree, countryOptions, staticCitiesByCountry, selectedCategories, selectedCountry, selectedCity, minPrice, maxPrice, minRating, sortBy, setSelectedCategories, setSelectedCategory, setSelectedCountry, setSelectedCity, setMinPrice, setMaxPrice, setMinRating, setSortBy]);

  

  // Apply filters and sorting
  const filteredItems = useMemo(() => {
    let list = Array.isArray(items) ? [...items] : [];

    const q = String(search || '').trim().toLowerCase();
    const cat = String(selectedCategory || '').trim().toLowerCase();
    const catSet = new Set((Array.isArray(selectedCategories) ? selectedCategories : []).map((n) => String(n).toLowerCase()));
    if (cat) catSet.add(cat);
    const loc = String(selectedLocation || '').trim().toLowerCase();
    const countrySel = String(selectedCountry || '').trim().toLowerCase();
    const citySel = String(selectedCity || '').trim().toLowerCase();
    const minP = Number(minPrice || 0);
    const maxP = Number(maxPrice || 0);
    const minR = Number(minRating || 0);

    function getPrice(svc) {
      const isPackage = Boolean(svc.is_package);
      const price = isPackage
        ? (svc.package_price ?? svc.base_price ?? null)
        : (svc.hourly_rate ?? svc.base_price ?? svc.price ?? null);
      return Number(price ?? 0);
    }

    function getCategoryName(svc) {
      return (svc?.category_id && categoryMap?.[svc.category_id]) || svc?.category || svc?.subcategory || svc?.mainCategory || '';
    }

    list = list.filter((s) => {
      // search (enhanced: stemming + synonyms + partial overlap)
      if (q) {
        const tUser = s?.creative?.user || s?.user || {};
        const tName = getPublicDisplayName(tUser) || s?.creative?.professional_title || s?.creative?.name || '';
        const rawText = [s.title, s.description, getCategoryName(s), tName].filter(Boolean).join(' ').toLowerCase();
        const normalizeWord = (w) => {
          let x = w.toLowerCase();
          x = x.replace(/ers$/,'er');
          x = x.replace(/ists$/,'ist');
          x = x.replace(/s$/,'');
          x = x.replace(/ing$/,'');
          x = x.replace(/er$/,'');
          return x;
        };
        const textTokens = rawText.replace(/[^a-zA-ZÅÄÖåäö0-9\s-]/g, ' ').split(/\s+/).filter(Boolean).map(normalizeWord);
        const textSet = new Set(textTokens);
        const synonymsRaw = {
          writer: ['writer','copywriter','copywriting','content writer','journalist'],
          editor: ['editor','editing','video editor'],
          photographer: ['photographer','photography'],
          videographer: ['videographer','videography','filmmaker'],
          designer: ['designer','design','ui','ux','ui ux','graphic','logo designer','product designer','web design'],
          seo: ['seo','search engine optimization','sem'],
          marketer: ['marketing','marketer','digital marketer','social media'],
          branding: ['branding','brand','brand strategist','art director','creative director'],
          developer: ['developer','frontend','backend','full stack']
        };
        const synonyms = Object.fromEntries(Object.entries(synonymsRaw).map(([k, arr]) => {
          const nk = normalizeWord(k);
          const normArr = Array.from(new Set(arr.map(normalizeWord)));
          return [nk, normArr];
        }));
        const queryTokens = String(q).replace(/[^a-zA-ZÅÄÖåäö0-9\s-]/g, ' ').split(/\s+/).filter(Boolean).map(normalizeWord);
        const expandedQueryTokens = new Set(queryTokens);
        queryTokens.forEach((qt) => {
          Object.entries(synonyms).forEach(([key, arr]) => {
            if (qt === key || arr.includes(qt)) {
              arr.forEach((syn) => expandedQueryTokens.add(syn));
            }
          });
        });
        const expanded = Array.from(expandedQueryTokens);
        if (expanded.length > 0) {
          let present = 0;
          expanded.forEach((t) => { if (textSet.has(t)) present++; });
          const ratio = present / expanded.length;
          if (!(ratio >= 0.5 || rawText.includes(q))) return false;
        } else {
          if (!rawText.includes(q)) return false;
        }
      }
      // category (multi-select) — parent selection includes its children
      if (catSet.size > 0) {
        const expanded = new Set(catSet);
        for (const sel of catSet) {
          const kids = childrenByParentLower.get(sel);
          if (kids && kids.size) kids.forEach((n) => expanded.add(n));
        }
        const serviceNames = new Set([
          String(getCategoryName(s) || '').toLowerCase(),
          String(s?.mainCategory || '').toLowerCase(),
          String(s?.subcategory || '').toLowerCase(),
        ].filter(Boolean));
        let match = false;
        for (const n of serviceNames) {
          if (expanded.has(n)) { match = true; break; }
        }
        if (!match) return false;
      }
      // location legacy
      if (loc) {
        const tUser = s?.creative?.user || s?.user || {};
        const countryFull = countryNameFromCode(tUser?.country_code || s?.creative?.country_code || '');
        const rawCity = s.city_name || tUser?.city_name || tUser?.city || s.city || s.cityName || '';
        const label = [rawCity, countryFull].filter(Boolean).join(', ').toLowerCase();
        if (!label.includes(loc)) return false;
      }
      // structured country/city
      if (countrySel) {
        const tUser = s?.creative?.user || s?.user || {};
        const fromCode = String(countryNameFromCode(tUser?.country_code || s?.creative?.country_code || '')).toLowerCase();
        const rawLoc = String(s.location || s.city_name || s.city || s.cityName || '').toLowerCase();
        const match = (fromCode && fromCode.includes(countrySel)) || (rawLoc && rawLoc.includes(countrySel));
        if (!match) {
          // If we cannot resolve any country label, do not exclude this item
          if (fromCode || rawLoc) return false;
        }
      }
      if (citySel) {
        const tUser = s?.creative?.user || s?.user || {};
        const cityId = tUser?.city_id || s?.city_id || s?.creative?.city_id;
        const idName = cityId ? String(cityByIdMap[String(cityId)] || '') : '';
        const rawCity = String(s.city_name || tUser?.city_name || tUser?.city || s.city || s.cityName || '');
        const labelNorm = normLower(idName || rawCity);
        const selNorm = normLower(selectedCity || '');
        // If we cannot resolve city label, skip filtering rather than exclude
        if (labelNorm && !labelNorm.includes(selNorm)) return false;
      }
      // price
      const p = getPrice(s);
      if (minP && p < minP) return false;
      if (maxP && p > maxP) return false;
      // rating
      const r = Number(s.rating ?? 0);
      if (minR && r < minR) return false;
      return true;
    });

    // sort
    if (sortBy === 'priceLowToHigh') {
      list.sort((a, b) => getPrice(a) - getPrice(b));
    } else if (sortBy === 'priceHighToLow') {
      list.sort((a, b) => getPrice(b) - getPrice(a));
    } else if (sortBy === 'highestRated') {
      list.sort((a, b) => Number(b.rating ?? 0) - Number(a.rating ?? 0));
    } else {
      // recommended/default: keep existing order (no randomization)
    }

    return list;
  }, [items, search, selectedCategory, selectedCategories, selectedLocation, selectedCountry, selectedCity, minPrice, maxPrice, minRating, sortBy, categoryMap, cityByIdMap, childrenByParentLower]);

  const countLabel = useMemo(() => {
    const n = Array.isArray(filteredItems) ? filteredItems.length : 0;
    return `${n} ${t('services.available', { defaultValue: 'services available' })}`;
  }, [filteredItems, t]);

  return (
    <>
      <section className="p-6 md:p-8 mb-4 text-center">
        <h1 className="text-2xl md:text-3xl font-bold text-primary-text">{t('services.viewAll', { defaultValue: 'View All Services' })}</h1>
        <p className="mt-1 text-sm text-secondary-text">{t('services.subtitle', { defaultValue: 'Browse thousands of services offered by skilled creatives' })}</p>
        <div className="mt-4 mb-6 relative max-w-4xl mx-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary-text" size={18} />
          <input
            ref={searchInputRef}
            type="text"
            value={String(search || '')}
            onFocus={() => { searchHadFocusRef.current = true; }}
            onBlur={() => { searchHadFocusRef.current = false; }}
            onChange={(e) => { searchSelectionRef.current = { start: e.target.selectionStart, end: e.target.selectionEnd }; setSearch(e.target.value); }}
            placeholder={t('services.searchServices', { defaultValue: 'Search services...' })}
            className="w-full h-11 rounded-lg bg-input-bg border border-card-border pl-10 pr-3 text-sm text-primary-text"
            aria-label={t('services.searchServices', { defaultValue: 'Search services...' })}
          />
        </div>
      </section>

      {/* Floating Filters icon (top-right, flush edge, more top margin) */}
      {!isFiltersOpen && (
        <button
          onClick={() => setIsFiltersOpen(true)}
          className="lg:hidden fixed top-24 md:top-28 right-0 z-40 rounded-l-full rounded-r-none border border-r-0 border-zinc-300 dark:border-zinc-700 bg-card-bg/90 backdrop-blur p-3 shadow-xl hover:bg-card-bg hover:shadow-2xl transition transform hover:-translate-y-0.5 text-primary-text inline-flex items-center"
          aria-label={t('services.filters', { defaultValue: 'Filters' })}
          aria-expanded={isFiltersOpen}
        >
          <Filter size={20} />
        </button>
      )}

      {isFiltersOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setIsFiltersOpen(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-[85%] max-w-sm bg-card-bg border-l border-card-border shadow-xl p-4 overflow-y-auto">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-primary-text">{t('services.filters', { defaultValue: 'Filters' })}</div>
              <div className="flex items-center gap-3">
                <button onClick={() => resetFilters()} className="text-xs text-secondary-text hover:underline">{t('services.clearAll', { defaultValue: 'Clear all' })}</button>
                <button onClick={() => setIsFiltersOpen(false)} className="text-secondary-text" aria-label={t('common.close', { defaultValue: 'Close' })}><X size={16} /></button>
              </div>
            </div>
            <div className="mt-4 space-y-4">
              {/* Price Range */}
              <div>
                <div className="text-xs text-secondary-text mb-2">{t('services.priceRange', { defaultValue: 'Price Range' })}</div>
                <div className="flex items-center gap-2">
                  <input type="number" min={0} value={String(minPriceInput)} onFocus={(e) => { minHadFocusRef.current = true; minInputRef.current = e.target; }} onBlur={() => { minHadFocusRef.current = false; commitMinPrice(); }} onChange={(e) => { const { selectionStart, selectionEnd } = e.target; minSelectionRef.current = { start: selectionStart, end: selectionEnd }; const val = e.target.value; setMinPriceInput(val); const v = String(val).trim(); if (!v) { if (minPrice !== '') setMinPrice(''); } else { const n = Number(v); const next = Number.isFinite(n) ? n : ''; if (minPrice !== next) setMinPrice(next); } }} onKeyDown={(e) => { if (e.key === 'Enter') commitMinPrice(); }} placeholder={t('services.minPrice', { defaultValue: 'Min' })} className="w-24 h-9 rounded-md bg-input-bg border border-card-border px-2 text-sm text-primary-text" />
                  <span className="text-xs text-secondary-text">-</span>
                  <input type="number" min={0} value={String(maxPriceInput)} onFocus={(e) => { maxHadFocusRef.current = true; maxInputRef.current = e.target; }} onBlur={() => { maxHadFocusRef.current = false; commitMaxPrice(); }} onChange={(e) => { const { selectionStart, selectionEnd } = e.target; maxSelectionRef.current = { start: selectionStart, end: selectionEnd }; const val = e.target.value; setMaxPriceInput(val); const v = String(val).trim(); if (!v) { if (maxPrice !== '') setMaxPrice(''); } else { const n = Number(v); const next = Number.isFinite(n) ? n : ''; if (maxPrice !== next) setMaxPrice(next); } }} onKeyDown={(e) => { if (e.key === 'Enter') commitMaxPrice(); }} placeholder={t('services.maxPrice', { defaultValue: 'Max' })} className="w-24 h-9 rounded-md bg-input-bg border border-card-border px-2 text-sm text-primary-text" />
                </div>
              </div>

              {/* Country & City */}
              <div>
                <div className="text-xs text-secondary-text mb-2">{t('services.country', { defaultValue: 'Country' })}</div>
                <select value={selectedCountry} onChange={(e) => setSelectedCountry(e.target.value)} className="w-full h-9 rounded-md bg-input-bg border border-card-border px-2 text-sm text-primary-text">
                  <option value="">All</option>
                  {countryOptions.map((c) => (<option key={c} value={c}>{c}</option>))}
                </select>
              </div>
              <div>
                <div className="text-xs text-secondary-text mb-2">{t('services.city', { defaultValue: 'City' })}</div>
                <select value={selectedCity} onChange={(e) => setSelectedCity(e.target.value)} disabled={!selectedCountry} className={`w-full h-9 rounded-md bg-input-bg border border-card-border px-2 text-sm text-primary-text ${!selectedCountry ? 'opacity-60 cursor-not-allowed' : ''}`}>
                  <option value="">All</option>
                  {cityOptions.map((c) => (<option key={c} value={c}>{c}</option>))}
                </select>
              </div>

              {/* Minimum Rating */}
              <div>
                <div className="text-xs text-secondary-text mb-2">{t('services.minimumRating', { defaultValue: 'Minimum Rating' })}</div>
                <div className="flex items-center gap-3">
                  <input type="range" min={0} max={5} step={1} value={Number(minRating || 0)} onChange={(e) => { const n = Number(e.target.value); if (minRating !== n) setMinRating(n); }} className="flex-1" aria-label="Minimum rating" />
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={16} className={i < Number(minRating || 0) ? 'text-yellow-400' : 'text-zinc-400'} />
                    ))}
                  </div>
                </div>
              </div>

              {/* Categories: parent/child hierarchy with multi-select */}
              <div>
                <div className="text-xs text-secondary-text mb-2">{t('services.categories', { defaultValue: 'Categories' })}</div>
                <div className="space-y-3">
                  {catTree.length === 0 && (
                    <div className="space-y-1">
                      {categoryOptions.map((c) => {
                        const checked = (selectedCategories || []).includes(c);
                        return (
                          <label key={c} className="flex items-center gap-2 text-sm text-primary-text">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => {
                                const current = new Set(selectedCategories || []);
                                if (current.has(c)) current.delete(c); else current.add(c);
                                setSelectedCategories(Array.from(current));
                                setSelectedCategory('');
                              }}
                            />
                            <span>{c}</span>
                            <span className="text-xs text-secondary-text">({categoryCounts[c] || 0})</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                  {catTree.map(({ parent, children }) => {
                    const pName = parent?.name || parent?.title || '';
                    const pChecked = (selectedCategories || []).includes(pName);
                    return (
                      <div key={parent.id || pName}>
                        <label className="flex items-center gap-2 text-sm text-primary-text">
                          <input type="checkbox" checked={pChecked} onChange={() => {
                            const current = new Set(selectedCategories || []);
                            if (current.has(pName)) current.delete(pName); else current.add(pName);
                            setSelectedCategories(Array.from(current));
                            setSelectedCategory('');
                          }} />
                          <span className="font-medium">{pName}</span>
                          <span className="text-xs text-secondary-text">({categoryCountsAgg[pName] || 0})</span>
                        </label>
                        {children && children.length > 0 && (
                          <div className="mt-1 pl-6 space-y-1">
                            {children.map((sc) => {
                              const scName = sc?.name || sc?.title || '';
                              const checked = (selectedCategories || []).includes(scName);
                              return (
                                <label key={sc.id || scName} className="flex items-center gap-2 text-sm text-primary-text">
                                  <input type="checkbox" checked={checked} onChange={() => {
                                    const current = new Set(selectedCategories || []);
                                    if (current.has(scName)) current.delete(scName); else current.add(scName);
                                    setSelectedCategories(Array.from(current));
                                    setSelectedCategory('');
                                  }} />
                                  <span>{scName}</span>
                                  <span className="text-xs text-secondary-text">({categoryCounts[scName] || 0})</span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <button onClick={() => setIsFiltersOpen(false)} className="rounded-md border border-card-border bg-input-bg px-3 py-2 text-sm">{t('common.done', { defaultValue: 'Done' })}</button>
            </div>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left: Sticky Filters */}
        <aside className="lg:col-span-3 hidden lg:block">
          <div className="bg-card-bg border border-card-border rounded-xl p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-primary-text">{t('services.filters', { defaultValue: 'Filters' })}</div>
              <button onClick={() => resetFilters()} className="text-xs text-secondary-text hover:underline">{t('services.clearAll', { defaultValue: 'Clear all' })}</button>
            </div>

            {/* Price Range */}
            <div>
              <div className="text-xs text-secondary-text mb-2">{t('services.priceRange', { defaultValue: 'Price Range' })}</div>
              <div className="flex items-center gap-2">
                <input type="number" min={0} value={String(minPriceInput)} onFocus={(e) => { minHadFocusRef.current = true; minInputRef.current = e.target; }} onBlur={() => { minHadFocusRef.current = false; commitMinPrice(); }} onChange={(e) => { const { selectionStart, selectionEnd } = e.target; minSelectionRef.current = { start: selectionStart, end: selectionEnd }; const val = e.target.value; setMinPriceInput(val); const v = String(val).trim(); if (!v) { if (minPrice !== '') setMinPrice(''); } else { const n = Number(v); const next = Number.isFinite(n) ? n : ''; if (minPrice !== next) setMinPrice(next); } }} onKeyDown={(e) => { if (e.key === 'Enter') commitMinPrice(); }} placeholder={t('services.minPrice', { defaultValue: 'Min' })} className="w-24 h-9 rounded-md bg-input-bg border border-card-border px-2 text-sm text-primary-text" />
                <span className="text-xs text-secondary-text">-</span>
                <input type="number" min={0} value={String(maxPriceInput)} onFocus={(e) => { maxHadFocusRef.current = true; maxInputRef.current = e.target; }} onBlur={() => { maxHadFocusRef.current = false; commitMaxPrice(); }} onChange={(e) => { const { selectionStart, selectionEnd } = e.target; maxSelectionRef.current = { start: selectionStart, end: selectionEnd }; const val = e.target.value; setMaxPriceInput(val); const v = String(val).trim(); if (!v) { if (maxPrice !== '') setMaxPrice(''); } else { const n = Number(v); const next = Number.isFinite(n) ? n : ''; if (maxPrice !== next) setMaxPrice(next); } }} onKeyDown={(e) => { if (e.key === 'Enter') commitMaxPrice(); }} placeholder={t('services.maxPrice', { defaultValue: 'Max' })} className="w-24 h-9 rounded-md bg-input-bg border border-card-border px-2 text-sm text-primary-text" />
              </div>
            </div>

            {/* Country & City */}
            <div>
              <div className="text-xs text-secondary-text mb-2">{t('services.country', { defaultValue: 'Country' })}</div>
              <select value={selectedCountry} onChange={(e) => setSelectedCountry(e.target.value)} className="w-full h-9 rounded-md bg-input-bg border border-card-border px-2 text-sm text-primary-text">
                <option value="">All</option>
                {countryOptions.map((c) => (<option key={c} value={c}>{c}</option>))}
              </select>
            </div>
            <div>
              <div className="text-xs text-secondary-text mb-2">{t('services.city', { defaultValue: 'City' })}</div>
              <select value={selectedCity} onChange={(e) => setSelectedCity(e.target.value)} disabled={!selectedCountry} className={`w-full h-9 rounded-md bg-input-bg border border-card-border px-2 text-sm text-primary-text ${!selectedCountry ? 'opacity-60 cursor-not-allowed' : ''}`}>
                <option value="">All</option>
                {cityOptions.map((c) => (<option key={c} value={c}>{c}</option>))}
              </select>
            </div>

            {/* Minimum Rating */}
            <div>
              <div className="text-xs text-secondary-text mb-2">{t('services.minimumRating', { defaultValue: 'Minimum Rating' })}</div>
              <div className="flex items-center gap-3">
                <input type="range" min={0} max={5} step={1} value={Number(minRating || 0)} onChange={(e) => { const n = Number(e.target.value); if (minRating !== n) setMinRating(n); }} className="flex-1" aria-label="Minimum rating" />
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={16} className={i < Number(minRating || 0) ? 'text-yellow-400' : 'text-zinc-400'} />
                  ))}
                </div>
              </div>
            </div>

            {/* Categories: parent/child hierarchy with multi-select */}
            <div>
              <div className="text-xs text-secondary-text mb-2">{t('services.categories', { defaultValue: 'Categories' })}</div>
              <div className="space-y-3">
                {catTree.length === 0 && (
                  <div className="space-y-1">
                    {categoryOptions.map((c) => {
                      const checked = (selectedCategories || []).includes(c);
                      return (
                        <label key={c} className="flex items-center gap-2 text-sm text-primary-text">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              const current = new Set(selectedCategories || []);
                              if (current.has(c)) current.delete(c); else current.add(c);
                              setSelectedCategories(Array.from(current));
                              setSelectedCategory('');
                            }}
                          />
                          <span>{c}</span>
                          <span className="text-xs text-secondary-text">({categoryCounts[c] || 0})</span>
                        </label>
                      );
                    })}
                  </div>
                )}
                {catTree.map(({ parent, children }) => {
                  const pName = parent?.name || parent?.title || '';
                  const pChecked = (selectedCategories || []).includes(pName);
                  return (
                    <div key={parent.id || pName}>
                      <label className="flex items-center gap-2 text-sm text-primary-text">
                        <input type="checkbox" checked={pChecked} onChange={() => {
                          const current = new Set(selectedCategories || []);
                          if (current.has(pName)) current.delete(pName); else current.add(pName);
                          setSelectedCategories(Array.from(current));
                          setSelectedCategory('');
                        }} />
                        <span className="font-medium">{pName}</span>
                        <span className="text-xs text-secondary-text">({categoryCountsAgg[pName] || 0})</span>
                      </label>
                      {children && children.length > 0 && (
                        <div className="mt-1 pl-6 space-y-1">
                          {children.map((sc) => {
                            const scName = sc?.name || sc?.title || '';
                            const checked = (selectedCategories || []).includes(scName);
                            return (
                              <label key={sc.id || scName} className="flex items-center gap-2 text-sm text-primary-text">
                                <input type="checkbox" checked={checked} onChange={() => {
                                  const current = new Set(selectedCategories || []);
                                  if (current.has(scName)) current.delete(scName); else current.add(scName);
                                  setSelectedCategories(Array.from(current));
                                  setSelectedCategory('');
                                }} />
                                <span>{scName}</span>
                                <span className="text-xs text-secondary-text">({categoryCounts[scName] || 0})</span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Advanced Filters removed per request */}
          </div>
      </aside>

      {/* Right: Results */}
      <section className="lg:col-span-9 space-y-6">

        {/* Top bar: Selected filters (wrap) + Clear + Sort */}
        <div className="flex flex-col gap-2 sticky top-0 z-20 bg-primary-bg py-2 -mx-2 px-2">
          {/* Filter chips */}
          <div className="flex flex-wrap items-center gap-2">
            {[
              ...((Array.isArray(selectedCategories) ? selectedCategories : [])).map((c) => ({ key: `cat:${c}`, label: c, onRemove: () => setSelectedCategories((selectedCategories || []).filter((x) => x !== c)) })),
              ...(selectedCountry ? [{ key: `country:${selectedCountry}`, label: selectedCountry, onRemove: () => setSelectedCountry('') }] : []),
              ...(selectedCity ? [{ key: `city:${selectedCity}`, label: selectedCity, onRemove: () => setSelectedCity('') }] : []),
              ...(minPrice ? [{ key: `min:${minPrice}`, label: `Min ${minPrice}`, onRemove: () => setMinPrice('') }] : []),
              ...(maxPrice ? [{ key: `max:${maxPrice}`, label: `Max ${maxPrice}`, onRemove: () => setMaxPrice('') }] : []),
              ...(minRating ? [{ key: `rating:${minRating}`, label: `${minRating}★`, onRemove: () => setMinRating('') }] : []),
            ].map((chip) => (
              <span key={chip.key} className="px-2 py-1 rounded-full bg-subtle-bg border border-card-border text-xs text-primary-text inline-flex items-center gap-1">
                {chip.label}
                <button onClick={chip.onRemove} aria-label="Remove" className="text-secondary-text hover:text-primary-text">
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
          {/* Clear + Sort row */}
          <div className="flex items-center justify-between gap-3">
            <button onClick={() => resetFilters()} className="text-xs text-secondary-text hover:underline whitespace-nowrap">
              {t('services.clearAll', { defaultValue: 'Clear all' })}
            </button>
            <div className="flex items-center gap-2">
              <label className="text-xs text-secondary-text hidden sm:inline" htmlFor="sort">{t('services.sortBy', { defaultValue: 'Sort by' })}</label>
              <select id="sort" value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="h-8 rounded-lg bg-input-bg border border-card-border px-2 text-sm text-primary-text">
                <option value="recommended">{t('services.recommended', { defaultValue: 'Recommended' })}</option>
                <option value="priceLowToHigh">{t('services.priceLowToHigh', { defaultValue: 'Price: Low to High' })}</option>
                <option value="priceHighToLow">{t('services.priceHighToLow', { defaultValue: 'Price: High to Low' })}</option>
                <option value="highestRated">{t('services.highestRated', { defaultValue: 'Highest Rated' })}</option>
              </select>
            </div>
          </div>
        </div>

        {/* Results header */}
        <div className="flex items-center justify-between">
          <div className="text-sm text-secondary-text">
            {loading ? t('services.loading', { defaultValue: 'Loading services...' }) : countLabel}
          </div>
          <div className="flex-1" />
          <div className="hidden" />
        </div>

        {error && <div className="text-sm text-red-400">{error}</div>}

        {/* Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4">
          {(!filteredItems || filteredItems.length === 0) && !loading ? (
            <div className="text-sm text-secondary-text">
              {t('services.noServicesFound', { defaultValue: 'No services found' })}
            </div>
          ) : (
            (Array.isArray(filteredItems) ? filteredItems : []).map((s) => {
              const creative = s.creative || null;
              const status = String(creative?.verification_status || '').toLowerCase();
              const verified = Boolean(creative?.verification_badge) || status === 'admin_approved' || status === 'verified' || status === 'approved';
              const tUser = creative?.user || null;
              const maskedName = (getPublicDisplayName(tUser))
                || creative?.professional_title
                || creative?.name
                || 'Creative';
              const proTitle = creative?.professional_title || '';
              const rating = Number(s.rating ?? 0);
              const reviews = Number(s.reviews ?? 0);
              const isPackage = Boolean(s.is_package);
              
              const imagesField = s.images;
              const imagesCandidateList = (() => {
                if (!imagesField) return [];
                if (Array.isArray(imagesField)) {
                  return imagesField
                    .map((x) => {
                      if (typeof x === 'string') return x;
                      if (x && typeof x === 'object') return x.url || x.src || x.image_url || '';
                      return '';
                    })
                    .filter(Boolean);
                }
                if (typeof imagesField === 'string') {
                  try {
                    const parsed = JSON.parse(imagesField);
                    if (Array.isArray(parsed)) {
                      return parsed
                        .map((x) => (typeof x === 'string' ? x : (x?.url || x?.src || x?.image_url || '')))
                        .filter(Boolean);
                    }
                  } catch (e) { void e; }
                  return imagesField.split(',').map((x) => x.trim()).filter(Boolean);
                }
                return [];
              })();
              const sid = String(s?.id || '');
              const imgErrored = Boolean(sid && imgErrorById[sid]);
              const rawPrimary = (
                s.image || s.image_url || s.imageUrl ||
                s.cover_image || s.cover_image_url || s.coverImage || s.coverImageUrl ||
                s.header_image || s.headerImage ||
                s.featured_image || s.featuredImage ||
                imagesCandidateList[0] || null
              );
              const normalizedPrimary = normalizeImageUrl(rawPrimary);
              const firstImageAlt = (() => {
                const f = imagesField;
                if (Array.isArray(f)) {
                  const obj = f.find((x) => x && typeof x === 'object' && (x.alt || x.title));
                  return (obj?.alt || obj?.title || '').trim();
                }
                if (typeof f === 'string') {
                  try {
                    const parsed = JSON.parse(f);
                    if (Array.isArray(parsed)) {
                      const obj = parsed.find((x) => x && typeof x === 'object' && (x.alt || x.title));
                      return (obj?.alt || obj?.title || '').trim();
                    }
                  } catch (e) { void e; }
                }
                return '';
              })();
              const hasPrimary = Boolean(normalizedPrimary) && !imgErrored;
              const placeholder = FRONTEND_ORIGIN ? `${FRONTEND_ORIGIN}/placeholder.jpg` : '/placeholder.jpg';
              const imageSrc = hasPrimary ? normalizedPrimary : placeholder;
              const hasImage = Boolean(imageSrc);
              const avatarSrc = tUser?.avatar_url || creative?.avatar || creative?.profile_image || '';
              const categoryName = (s?.category_id && categoryMap?.[s.category_id]) || s?.category || s?.subcategory || s?.mainCategory;
              const categoryIconKey = (() => {
                const list = Array.isArray(categoriesList) ? categoriesList : [];
                const cid = s?.category_id;
                // Try top-level match by id
                let iconKey = list.find((c) => c && c.id === cid)?.icon_url || list.find((c) => c && c.id === cid)?.icon || null;
                if (iconKey) return iconKey;
                // Try subcategory match by id or name
                for (const c of list) {
                  const sub = (c?.subcategories || []).find((sc) => sc?.id === cid || (categoryName && (sc?.name === categoryName)));
                  if (sub) return sub.icon_url || sub.icon || c.icon_url || c.icon || null;
                }
                // Fallback: match by name
                const byName = list.find((c) => (c?.name || c?.title) === categoryName);
                return (byName?.icon_url || byName?.icon || null);
              })();
              const rawCity = s.city_name || tUser?.city_name || tUser?.city || creative?.city_name || creative?.city || s.city || s.cityName || '';
              const cityIdCandidate = tUser?.city_id || s?.city_id || creative?.city_id || null;
              const cityById = cityIdCandidate ? (cityByIdMap[String(cityIdCandidate)] || '') : '';
              const cityName = cityById || rawCity;
              const countryFull = countryNameFromCode(tUser?.country_code || creative?.country_code || '');
              const locationLabel = [cityName, countryFull].filter(Boolean).join(', ');
              
              const isHourly = !isPackage && s.hourly_rate != null;
              const serviceCurrency = String(s.currency || 'SEK').toUpperCase();
              const displayPrice = isPackage
                ? (s.package_price ?? s.base_price ?? null)
                : (isHourly ? s.hourly_rate : (s.base_price ?? s.price ?? null));
              // Convert price to user's selected currency
              const convertedPrice = displayPrice != null ? convert(displayPrice, serviceCurrency, userCurrency) : null;
              const priceLabel = convertedPrice != null
                ? `${formatPrice(convertedPrice, userCurrency)}${isHourly ? '/HR' : ''}`
                : '—';
              return (
                <div key={s.id} className="bg-card-bg card-hover rounded-xl shadow-md border border-card-border h-full flex flex-col overflow-hidden">
                  <div className="relative">
                    {hasImage ? (
                      <img
                        src={imageSrc}
                        alt={`${firstImageAlt || s.title || 'Service'} image`}
                        loading="lazy"
                        onError={() => { const key = String(s?.id || ''); if (key) setImgErrorById((prev) => ({ ...prev, [key]: true })); }}
                        className="w-full h-40 md:h-44 object-cover border-b border-card-border"
                      />
                    ) : (
                      <div className="w-full h-40 md:h-44 border-b border-card-border bg-gradient-to-r from-zinc-900 via-zinc-800 to-zinc-700" aria-hidden="true" />
                    )}
                    {/* Dim overlay for readability (stronger at the bottom) */}
                    {hasImage && (
                      <div className="image-overlay overlay-gradient-bottom" aria-hidden="true" />
                    )}

                    {/* Verified pill at top-left */}
                    {verified && (
                      <div className="absolute top-2 left-2 bg-black/60 text-white text-xs rounded-full px-2 py-1 inline-flex items-center gap-1">
                        <ShieldCheck size={14} className="text-emerald-400" />
                        <span>Verified</span>
                      </div>
                    )}
                    {/* Header moved to bottom of image: title + category */}
                    <div className="absolute bottom-0 left-0 right-0 px-3 py-2 text-white">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm md:text-base line-clamp-2 drop-shadow-sm">
                            {s.title || s.name || t('services.untitled', { defaultValue: 'Untitled Service' })}
                          </div>
                          {categoryName && (
                            <div className="mt-0.5 text-[11px] inline-flex items-center gap-1 drop-shadow-sm">
                              <span className="px-2 py-0.5 rounded-full border border-white/30 bg-black/30 inline-flex items-center gap-1">
                                {categoryIconKey ? (
                                  <CategoryIcon iconKey={categoryIconKey} name={categoryName} size={12} />
                                ) : (
                                  <CategoryIcon iconKey={"md:MdCategory"} name={categoryName} size={12} />
                                )}
                                <span>{categoryName}</span>
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      {/* Bottom-left: price + delivery text */}
                      <div className="mt-1 flex items-center gap-3 text-[12px]">
                        <span className="inline-flex items-center gap-1">
                          <BadgeDollarSign size={14} />
                          {priceLabel}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <Star size={14} className="text-yellow-400" />
                          {rating.toFixed(1)} ({reviews})
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="p-3 flex-1 flex flex-col">
                    {/* Talent avatar + name */}
                    <div className="flex items-center gap-3">
                      {avatarSrc ? (
                        <img
                          src={avatarSrc}
                          alt={`${maskedName} - Creative professional profile picture`}
                          loading="lazy"
                          onError={(e) => { e.currentTarget.src = '/Talents/Talents-1.jpg'; }}
                          className="w-9 h-9 rounded-full object-cover border border-card-border"
                        />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-subtle-bg border border-card-border flex items-center justify-center text-sm font-semibold text-primary-text">
                          {initialsOf(maskedName)}
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="text-sm text-primary-text font-medium truncate">{maskedName}</div>
                        {proTitle && (
                          <div className="mt-0.5 text-xs text-secondary-text line-clamp-2">{proTitle}</div>
                        )}
                      </div>
                    </div>

                    {/* Description removed per request */}

                    {/* Details row: left shows address, right shows CTA */}
                    <div className="mt-auto pt-3 flex items-center justify-between">
                      <div className="text-xs text-secondary-text inline-flex items-center gap-1">
                        {locationLabel && (
                          <>
                            <MapPin size={14} />
                            <span>{locationLabel}</span>
                          </>
                        )}
                      </div>
                      {(s.id) ? (() => {
                        const titleSlug = slugify(s.title || '');
                        const categorySlug = categoryName ? slugify(categoryName) : '';
                        const talentInitials = initialsOf(maskedName);
                        const baseSlug = titleSlug || categorySlug;
                        const idText = String(s.id || '').toLowerCase().replace(/[^0-9a-f-]/g, '');
                        const idFirst = idText.replace(/-/g, '').slice(0, 6);
                        const idLast = idText.replace(/-/g, '').slice(-6);
                        const idToken = `${idFirst}${idLast}`; // 12 hex chars: first6 + last6
                        const toPath = baseSlug
                          ? `/services/${baseSlug}${talentInitials ? `-${talentInitials}` : ''}-${idToken}`
                          : `/services/${idToken}`;
                        return (
                          <Link to={toPath} className="text-sm text-primary-text hover:underline">
                            {t('services.viewDetail', { defaultValue: 'View Detail' })}
                          </Link>
                        );
                      })() : (
                        <span className="text-sm text-secondary-text">{t('services.view', { defaultValue: 'View' })}</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
        {/* Infinite loader sentinel */}
        <div ref={loaderRef} className="py-4 text-center">
          {loadingMore && (
            <div className="text-sm text-secondary-text">{t('services.loadingMore', { defaultValue: 'Loading more...' })}</div>
          )}
          {!hasMore && !loading && (
            <div className="text-xs text-secondary-text">{t('services.noMore', { defaultValue: 'No more services' })}</div>
          )}
        </div>
      </section>
    </div>
    </>
  );
}
// Small renderer for category icons supporting react-icons key or image URL
function CategoryIcon({ iconKey, name, size = 12 }) {
  const DefaultComp = useIcon('md:MdCategory');
  const IconComp = useIcon(iconKey);
  const [imgError, setImgError] = useState(false);
  const isUrl = (v) => typeof v === 'string' && (/^https?:\/\//.test(v) || v.startsWith('/') || v.startsWith('./') || v.startsWith('../'));
  if (IconComp) return <IconComp size={size} />;
  if (isUrl(iconKey) && !imgError) {
    return (
      <img
        src={iconKey}
        alt={`${name || 'Service category'} icon`}
        className="w-3.5 h-3.5 object-contain"
        onError={() => setImgError(true)}
      />
    );
  }
  if (DefaultComp) return <DefaultComp size={size} />;
  return <Tag size={size} />;
}
